import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { PackagesService } from '../services/payments/packages/packages.service';
import { PackagesResponse } from '../models/payment/packagesResponse';
import { apiResponse } from '../models/apiResponse';
import { MessageService } from 'primeng/api';
import { PromocodesService } from '../services/payments/promocodes/promocodes.service';
import { PromoCode, PromoType } from '../models/payment/promocode.model';
import { CreatePayNowRequest } from '../models/payment/createPayNowRequest';
import { PaymentGatewayResponse } from '../models/payment/paymentGatewayResponse.model';
import { PaymentService } from '../services/payments/payment/payment.service';
import { FooterComponent } from '../website-layout/footer/footer.component';
import { PackageCategoryEnum, DurationTypeEnum } from '../models/payment/package-enums.model';

interface Package {
  label: string;
  value: string;
  price: number;
  description: string;
  duration: string;
  categoryName: string;
  services: string[];
}

@Component({
  selector: 'app-pay-now',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    DropdownModule,
    ButtonModule,
    CardModule,
    FooterComponent
  ],
  templateUrl: './pay-now.component.html',
  styleUrls: ['./pay-now.component.scss']
})
export class PayNowComponent implements OnInit {
  payNowForm!: FormGroup;
  formSubmitted = false;

  packages: Package[] = [];

  selectedPackage: Package | null = null;

  appliedPromo: PromoCode | null = null;
  discountApplied: boolean = false;
  promoError: string = '';
  isValidatingPromo: boolean = false;
  discountAmount: number = 0;
  readonly GST_RATE = 0.18;

  isProcessingPayment = false;

  categoryOptions = [
    { label: 'Undergraduate Admissions', value: PackageCategoryEnum.UndergraduateAdmissions },
    { label: 'Postgraduate Admissions', value: PackageCategoryEnum.PostgraduateAdmissions },
    { label: 'Post Admission Services', value: PackageCategoryEnum.PostAdmissionServices },
    { label: 'Housing', value: PackageCategoryEnum.Housing },
    { label: 'Education Loan', value: PackageCategoryEnum.EducationLoan },
    { label: 'Education Counselling', value: PackageCategoryEnum.EducationCounselling },
    { label: 'Writing Services', value: PackageCategoryEnum.WritingServices }
  ];

  constructor(private fb: FormBuilder
    , private packageService: PackagesService
    , private messageService: MessageService
    , private promocodeService: PromocodesService
    , private paymentService: PaymentService
  ) { }

  ngOnInit(): void {
    this.buildForm();
    this.getActivePackages();
  }

  buildForm(): void {
    this.payNowForm = this.fb.group({
      // Personal Details
      fullName: ['', [Validators.required, Validators.minLength(3)]],
      emailAddress: ['', [Validators.required, Validators.email]],
      contactNumber: ['', [Validators.required, Validators.pattern('^[0-9]{10}$')]],

      // Billing Address
      billingAddress: ['', [Validators.required]],

      // Package & Promo Code
      selectedPackage: ['', Validators.required],
      promoCode: ['']
    });
  }

  getActivePackages(): void {
    this.packageService.getActivePackages().subscribe({
      next: (data: apiResponse<PackagesResponse[]>) => {
        this.packages = data.result.map(pkg => ({
          label: `${pkg.name} - ₹${pkg.amount.toLocaleString('en-IN')} + GST`,
          value: pkg.id.toString(),
          price: pkg.amount,
          description: pkg.description,
          duration: this.formatDuration(pkg.duration, pkg.durationType),
          categoryName: this.getCategoryNames(pkg.categories),
          services: pkg.servicesIncluded
        }));
      },
      error: (err) => {
        console.error('Failed to load packages', err);
        this.packages = [];
        this.showError('Failed to load packages');
      }
    });
  }

  private getCategoryNames(categories: number[]): string {
    if (!categories || categories.length === 0) return '';
    return categories
      .map(c => this.categoryOptions.find(opt => opt.value === c)?.label || '')
      .filter(label => label !== '')
      .join(', ');
  }

  private formatDuration(duration: number | string, durationType: number): string {
    const isSingle = Number(duration) === 1;
    if (durationType === DurationTypeEnum.Days) {
      return `${duration} ${isSingle ? 'Day' : 'Days'}`;
    }
    return `${duration} ${isSingle ? 'Month' : 'Months'}`;
  }

  onPackageChange(event: any): void {
    const packageValue = event.value;
    if (packageValue) {
      this.selectedPackage = this.packages.find(pkg => pkg.value === packageValue) || null;
    } else {
      this.selectedPackage = null;
    }
    if (this.discountApplied) {
      this.removePromoCode();
    } else {
      this.discountAmount = 0;
    }
  }

  applyPromoCode(): void {
    this.promoError = ''; // Reset error

    // Check if package is selected
    if (!this.selectedPackage) {
      this.promoError = 'Please select a package first.';
      this.payNowForm.get('promoCode')?.markAsTouched();
      return;
    }

    const code = this.payNowForm.get('promoCode')?.value;

    if (code && code.trim()) {
      this.isValidatingPromo = true;
      this.promocodeService.validatePromocodeByCode(code).subscribe({
        next: (response: any) => {
          // Handle response wrapper
          const promo: PromoCode = this.normalizePromoCode(response);
          this.appliedPromo = promo;
          this.discountApplied = true;
          this.isValidatingPromo = false;
          this.payNowForm.get('promoCode')?.disable();
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Promo code applied!' });
          this.updateCalculations();
        },
        error: (error: any) => {
          this.isValidatingPromo = false;
          this.discountApplied = false;
          console.log('Promo Code Validation Error:', error);
          this.promoError = error?.message || 'Invalid Promo Code';
        }
      });
    }
  }

  removePromoCode(): void {
    this.discountApplied = false;
    this.appliedPromo = null;
    this.promoError = '';
    this.discountAmount = 0;

    const control = this.payNowForm.get('promoCode');
    control?.enable();
    control?.setValue('');
  }

 get taxableAmount(): number {
  return this.selectedPackage ? this.selectedPackage.price - this.discountAmount : 0;
}

get totalAmount(): number {
  return +(this.taxableAmount * (1 + this.GST_RATE)).toFixed(2);
}

get gstAmount(): number {
  return +(this.totalAmount - this.taxableAmount).toFixed(2);
}

formatINR(value: number): string {
  return value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

  get hasSelectedPackage(): boolean {
    return this.selectedPackage !== null;
  }

  onSubmit(): void {
    this.formSubmitted = true;

    this.payNowForm.markAllAsTouched();

    if (this.payNowForm.invalid) {
      this.showError('Please fill in all required fields correctly.');
      return;
    }

    if (!this.selectedPackage) {
      this.showError('Please select a package.');
      return;
    }

    this.isProcessingPayment = true;

    const request: CreatePayNowRequest = {
      name: this.payNowForm.get('fullName')?.value,
      userEmail: this.payNowForm.get('emailAddress')?.value,
      phoneNumber: this.payNowForm.get('contactNumber')?.value,
      amount: this.totalAmount,
      transactionType: 'SALE',
      packageId: Number(this.selectedPackage.value),
      discountId: this.appliedPromo ? this.appliedPromo.id : undefined,
      amountBeforeDiscount: this.selectedPackage.price,
      billingAddress: {
        billAddress: this.payNowForm.get('billingAddress')?.value
      }
    };

    this.paymentService.payNow(request).subscribe({
      next: (response: PaymentGatewayResponse) => {
        // this.isProcessingPayment = false;
        //Disbled it because we are redirecting to the payment gateway

        if (response.redirectUri && response.redirectUri.trim() !== '') {
          // Redirect the user to the Payment Gateway
          window.location.href = response.redirectUri;
        } else {
          const errorMessage = response.responseDescription || 'Payment gateway error occurred. Please try again.';
          this.showError(errorMessage);
        }
      },
      error: (error: any) => {
        this.isProcessingPayment = false;
        console.error('Payment Failed:', error);

        // Handle generic or network errors
        const errorMessage = error?.message || 'Failed to process payment. Please try again.';
        this.showError(errorMessage);
      }
    });
  }

  formatPhoneNumber(event: any): void {
    let value = event.target.value.replace(/\D/g, '');
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
    this.payNowForm.patchValue({ contactNumber: value }, { emitEvent: false });
  }

  // formatPinCode(event: any): void {
  //   let value = event.target.value.replace(/\D/g, '');
  //   if (value.length > 6) {
  //     value = value.substring(0, 6);
  //   }
  //   this.payNowForm.patchValue({ pinCode: value }, { emitEvent: false });
  // }

  private updateCalculations(): void {
    if (!this.discountApplied || !this.selectedPackage || !this.appliedPromo) {
      this.discountAmount = 0;
      return;
    }

    if (this.appliedPromo.type === PromoType.Percentage) {
      this.discountAmount = (this.selectedPackage.price * this.appliedPromo.value) / 100;
    } else {
      // Fixed Amount
      this.discountAmount = this.appliedPromo.value;
    }
  }

  private showError(detail: string) {
    this.messageService.add({ severity: 'error', summary: 'Error', detail });
  }

  private normalizePromoCode(p: any): PromoCode {
    const isActive = p.status === 0;

    return {
      id: p.Id ?? p.id ?? p.DiscountId ?? p.discountId ?? 0,
      code: p.Code ?? p.code ?? '',
      description: p.Description ?? p.description ?? '',
      type: p.Type ?? p.type ?? 0,
      value: p.Value ?? p.value ?? 0,
      usageLimit: p.UsageLimit ?? p.usageLimit ?? 0,
      usageCount: p.UsageCount ?? p.usageCount ?? 0,
      validityFrom: p.ValidityFrom ?? p.validityFrom ?? '',
      validityTo: p.ValidityTo ?? p.validityTo ?? '',
      isActive: isActive,
      status: p.status === 0 ? 'Active' : 'Inactive'
    };
  }
}

